import {
    getDefectSignature,
    listDefectSignatures,
} from './queries';
import {
    deleteDefectSignature,
    updateDefectSignature,
} from './mutations';

import {GraphQLQuery} from '@aws-amplify/api';

import {
    DefectSignature,
    DefectSignaturesFilter,
    DeleteDefectSignature,
    GetDefectSignature,
    ListDefectSignatures,
    UpdateDefectSignature,
} from '../types.ts';
import {getTestingApiClient} from '../../testing-api-integration/client.ts';

// FNP-904. Reuses the FNP-859 testing-api client — there is deliberately no
// second client, same as the test-observations module.
//
// Read + a narrow triage write (status/jiraKey) + FNP-969 delete. The console
// still never CREATES a signature: rows are produced by the sweep from evidence,
// and a hand-authored one would carry a fingerprint matching nothing.

// DefectSignaturesFilterInput accepts only {actionCode, status, verificationResult}.
// Empty strings are dropped rather than sent: an empty enum value fails AppSync
// validation and an empty String narrows to nothing.
const normalizeFilter = (filter: DefectSignaturesFilter): DefectSignaturesFilter =>
    Object.entries(filter)
          .reduce((acc: Record<string, string>, [key, value]) => {
              if (typeof value === 'string' && value !== '') {
                  acc[key] = value;
              }
              return acc;
          }, {});

export const fetchRecords = async (
    filter: DefectSignaturesFilter = {},
    limit?: number,
    nextToken?: string | null,
) => {
    const normalized = normalizeFilter(filter);

    const response = await getTestingApiClient().graphql<GraphQLQuery<ListDefectSignatures>>({
        query: listDefectSignatures,
        variables: {
            // Omit `filter` entirely when nothing is active — sending {} makes the
            // resolver build an empty FilterExpression.
            ...(Object.keys(normalized).length ? {filter: normalized} : {}),
            ...(typeof limit === 'number' ? {limit} : {}),
            ...(nextToken ? {nextToken} : {}),
        },
    });

    return {
        // Defensive: a GraphQL-level error leaves data undefined. The caller must
        // always receive a real array so the table never renders an undefined row.
        items: response.data?.listDefectSignatures?.items ?? [],
        nextToken: response.data?.listDefectSignatures?.nextToken ?? null,
    };
};

export const fetch = async (id: string) => {
    const response = await getTestingApiClient().graphql<GraphQLQuery<GetDefectSignature>>({
        query: getDefectSignature,
        variables: {id: id},
    });
    return response.data?.getDefectSignature;
};

// Human triage: attach a Jira key and/or move the status. WHITELISTED payload —
// only the three fields the input accepts are ever sent, never a spread of the
// record (the platform's mutation-payload rule).
//
// `jiraKey` is omitted when empty rather than sent as null: the resolver only
// writes the attribute when a value is supplied, so omitting it LEAVES an
// existing key in place instead of erasing it.
export const update = async ({
                                 id,
                                 status,
                                 jiraKey,
                             }: Pick<DefectSignature, 'id' | 'status'> & { jiraKey?: string | null }) => {
    const response = await getTestingApiClient().graphql<GraphQLQuery<UpdateDefectSignature>>({
        query: updateDefectSignature,
        variables: {
            input: {
                id: id,
                status: status,
                ...(typeof jiraKey === 'string' && jiraKey.trim() !== ''
                    ? {jiraKey: jiraKey.trim()}
                    : {}),
            },
        },
    });
    return response.data?.updateDefectSignature;
};

// Discards the signature AND the record of how often it fired. This is for
// clearing an environment — it is NOT the "this defect is fixed" path (that is
// resolveDefectSignature, concluded by the sweep, with no console affordance).
export const deleteRecord = async (id: string) => {
    const response = await getTestingApiClient().graphql<GraphQLQuery<DeleteDefectSignature>>({
        query: deleteDefectSignature,
        variables: {id: id},
    });
    return response.data?.deleteDefectSignature;
};
